import type {
  ReferenceColumnConfig,
  ReferenceFilterKind,
  ReferencePageConfig,
  ReferenceValueKind,
} from '@/components/references/types'

/**
 * Config-driven column model for the Procurement References switchboard
 * (Access `form_frmProcurementMainSubFormReferences.txt`, combobox `cbPreferencesList`).
 * Mirrors `saleReferenceConfig.ts` / `purchaseReferenceConfig.ts`. Columns + order are
 * taken verbatim from each `form_frmProcurementQryRef*.txt` (ControlSource order).
 */
export type ProcurementReferencePageKey =
  | 'ContractStatistics'
  | 'PendingPayment'
  | 'VirtualWarehouse'
  | 'Received'
  | 'Consignment'
  | 'PTBFFixation'
  | 'CSFixation'
  | 'CPList'
  | 'CPBank'
  | 'FuturesAllocation'
  | 'ListandValo'
  | 'FuturesList'
  | 'Bank'
  | 'Package'
  | 'DeliveryPlace'
  | 'Certification'

// Types unified vào module dùng chung `@/components/references`. Giữ alias tên cũ.
export type ProcurementReferenceValueKind = ReferenceValueKind
export type ProcurementReferenceColumnConfig = ReferenceColumnConfig
export type ProcurementReferencePageConfig = ReferencePageConfig<ProcurementReferencePageKey>

/** Access combobox order (VBA `ChangeDatasheet`). */
export const PROCUREMENT_REFERENCE_PAGE_ORDER: ProcurementReferencePageKey[] = [
  'ContractStatistics',
  'PendingPayment',
  'VirtualWarehouse',
  'Received',
  'Consignment',
  'PTBFFixation',
  'CSFixation',
  'CPList',
  'CPBank',
  'FuturesAllocation',
  'ListandValo',
  'FuturesList',
  'Bank',
  'Package',
  'DeliveryPlace',
  'Certification',
]

/**
 * Filter mỗi page forward vào API (page không khai → không gửi filter + header
 * disable). Chỉ 2 page dùng shared "Inbound Logistics" filter (date + forced-close).
 */
export const PROCUREMENT_REFERENCE_PAGE_FILTERS: Partial<
  Record<ProcurementReferencePageKey, ReferenceFilterKind[]>
> = {
  ContractStatistics: ['date', 'forcedClose'],
  PendingPayment: ['date', 'forcedClose'],
}

// ---- Column-set builders -------------------------------------------------
// Weight/quantity columns (kg) → 2 decimals; quality percentages → 2 decimals;
// counts (bags/lots) → 0. Updated Date = datetime, Updated Person = text.

const upd = (): ProcurementReferenceColumnConfig[] => [
  { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
  { field: 'person_update', headerName: 'Updated Person', minWidth: 150, flex: 1, proper: true },
]

const num = (
  field: string,
  headerName: string,
  precision = 2,
  width = 130,
  sum = false,
): ProcurementReferenceColumnConfig => ({ field, headerName, kind: 'number', precision, width, sum })

// Virtual Warehouse / Received quality columns share the same numeric grades.
const QUALITY_FIELDS_VW = [
  'mc', 'bb', 'brown', 'cherry', 'overdry', 'black', 'brokens', 'foreign', 'insect',
  'immature', 'old', 'fm', 'mouldy', 'white', 's18', 's17', 's16', 's15', 's14',
  's13', 's12', 'under_s12',
]
const QUALITY_FIELDS_RCV = [
  'mc_on_kett', 'mc', 'blacks', 'brokens', 'black_and_broken', 'fm', 'mouldy', 'brown',
  'bleach', 'partly_black', 'immature', 'broken_over_1_per_2', 'pulper_nipped_beans',
  'blotchy_beans', 'waxy_beans', 'insect_beans', 'triages_defects', 'fb', 'overdry',
  'cherry', 'yellowbeans', 'polish_degree', 's18', 's16', 's15', 's14', 's13', 's12',
  'under_s12',
]

const titleize = (f: string) => f.split('_').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ')

export const PROCUREMENT_REFERENCE_PAGE_CONFIGS: Record<
  ProcurementReferencePageKey,
  ProcurementReferencePageConfig
> = {
  ContractStatistics: {
    pageKey: 'ContractStatistics',
    label: 'Contract Statistics',
    sourceForm: 'frmProcurementQryReportContract',
    sourceQuery: 'ProcurementQryReportContract',
    // Thứ tự cột bám đúng Access `frmProcurementQryReportContract` (ColumnOrder 0→14),
    // khớp với thứ tự BE SQL (procurement_reference_pg.py ContractStatistics).
    columns: [
      { field: 'contract_no', headerName: 'Contract No', width: 150 },
      { field: 'npe_ref', headerName: 'NPE.No Ref', width: 150 },
      { field: 'contract_date', headerName: 'Contract Date', kind: 'date', width: 130 },
      { field: 'name', headerName: 'CP Name', minWidth: 200, flex: 1, proper: true, wrap: true },
      { field: 'quality_code', headerName: 'Quality Code', width: 130 },
      num('paydata_paid', 'Paid - From PayData', 0, 160, true),
      num('npe_to_p_paid', 'Paid - NPE to Purchase', 0, 170, true),
      num('p_from_npe_paid', 'Paid - Purchase from NPE', 0, 180, true),
      num('real_paid', 'Total Final Paid', 0, 150, true),
      num('to_be_pay', 'To be Pay', 0, 140, true),
      num('kg_fixed', 'Quantity - Fixed', 0, 150, true),
      num('received', 'Quantity - Received', 0, 160, true),
      num('tolerance', 'Quantity - Pending', 0, 160, true),
      num('kg_to_fix', 'Quantity - To be fixed', 0, 170, true),
      { field: 'delivery_place', headerName: 'Delivery Place', minWidth: 180, flex: 1, wrap: true },
    ],
  },
  PendingPayment: {
    pageKey: 'PendingPayment',
    label: 'Pending Payment',
    sourceForm: 'frmProcurementQryRefGoodAlc_PendingPayment',
    sourceQuery: 'public_v_goods_allocation_pending',
    // Access `frmProcurementQryRefGoodAlc_PendingPayment` — form có KHÔNG có
    // ColumnOrder tĩnh (chỉ TextBox, ColumnHidden restore runtime từ
    // `public_global_filter`), nên dùng `TabIndex` per control làm thứ tự
    // (Access mặc định ColumnOrder = TabIndex khi chưa từng tự kéo sắp cột):
    // id(0) contract_no(1) m_date(2) supplier(3) truck_no(4) grade(5)
    // to_warehouse(6) truck_status(7) truck_id(8) to_be_requested(9).
    columns: [
      { field: 'id', headerName: 'GoodsAllocate ID', width: 140 },
      { field: 'contract_no', headerName: 'Contract No', width: 150 },
      { field: 'm_date', headerName: 'M Date', kind: 'date', width: 130 },
      { field: 'supplier', headerName: 'Supplier', minWidth: 200, flex: 1, proper: true, wrap: true },
      { field: 'truck_no', headerName: 'Truck No', width: 130 },
      { field: 'grade', headerName: 'Grade', width: 130 },
      { field: 'to_warehouse', headerName: 'To Warehouse', minWidth: 160, flex: 1, wrap: true },
      { field: 'truck_status', headerName: 'Truck Status', width: 140 },
      { field: 'truck_id', headerName: 'Truck ID', width: 130 },
      num('to_be_requested', 'To be request (Kg)', 2, 160, true),
    ],
  },
  VirtualWarehouse: {
    pageKey: 'VirtualWarehouse',
    label: 'Virtual Warehouse',
    sourceForm: 'frmProcurementQryRefVirtualWarehouse',
    sourceQuery: 'ProcurementQryRefVirtualWarehouse',
    columns: [
      { field: 'receipt_no', headerName: 'Receipt No', width: 140 },
      { field: 'date_in', headerName: 'Date In', kind: 'date', width: 120 },
      { field: 'truck_no', headerName: 'Truck No', width: 120 },
      { field: 'do_no', headerName: 'DO No', width: 130 },
      { field: 'supplier_code', headerName: 'Supplier Code', width: 140 },
      { field: 'client_code', headerName: 'Client Code', width: 130 },
      { field: 'warehouse_code', headerName: 'Warehouse Code', width: 150 },
      { field: 'quality_code', headerName: 'Quality Code', width: 130 },
      num('announced_quantity', 'Announced Quantity', 2, 160),
      num('no_of_bag', 'No of Bag', 0, 120),
      num('vgm_quantity_kg', 'VGM Quantity Kg', 2, 150),
      ...QUALITY_FIELDS_VW.map((f) => num(f, titleize(f), 2, 100)),
      num('deduction_quantity_kg', 'Deduction Quantity Kg', 2, 180),
      num('deduction_price_vnd', 'Deduction Price VND', 2, 170),
      { field: 'note', headerName: 'Note', minWidth: 180, flex: 1, wrap: true },
      { field: 'updated_date', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'updated_by', headerName: 'Updated By', minWidth: 140, flex: 1, proper: true },
      { field: 'created_date', headerName: 'Created Date', kind: 'datetime', width: 160 },
      { field: 'created_by', headerName: 'Created By', minWidth: 140, flex: 1, proper: true },
    ],
  },
  Received: {
    pageKey: 'Received',
    label: 'Received',
    sourceForm: 'frmProcurementQryRefReceive',
    sourceQuery: 'ProcurementQryRefReceive',
    columns: [
      { field: 'grn_id', headerName: 'GRN ID', width: 120 },
      { field: 'warehouse', headerName: 'Warehouse', minWidth: 150, flex: 1, wrap: true },
      { field: 'date_in', headerName: 'Date In', kind: 'date', width: 120 },
      { field: 'suppliers', headerName: 'Suppliers', minWidth: 180, flex: 1, proper: true, wrap: true },
      { field: 'order_no', headerName: 'Order No', width: 140 },
      { field: 'quality_code', headerName: 'Quality Code', width: 130 },
      { field: 'truck_no', headerName: 'Truck No', width: 120 },
      { field: 'origin', headerName: 'Origin', width: 130 },
      { field: 'package', headerName: 'Package', width: 130 },
      num('gross_quantity', 'Gross Quantity', 2, 140),
      num('kg_per_bag', 'Kg Per Bag', 2, 120),
      num('no_of_bag', 'No of Bag', 0, 120),
      num('net_quantity_kg', 'Net Quantity Kg', 2, 150),
      ...QUALITY_FIELDS_RCV.map((f) => num(f, titleize(f), 2, 110)),
      { field: 'cuptest', headerName: 'Cuptest', minWidth: 140, flex: 1, wrap: true },
      { field: 'qc_name', headerName: 'QC Name', minWidth: 140, flex: 1, proper: true, wrap: true },
      { field: 'area', headerName: 'Area', width: 120 },
      { field: 'stack_id', headerName: 'Stack ID', width: 120 },
      num('deduction_quantity_kg', 'Deduction Quantity Kg', 2, 180),
      { field: 'note', headerName: 'Note', minWidth: 180, flex: 1, wrap: true },
      { field: 'inspection_company', headerName: 'Inspection Company', minWidth: 180, flex: 1, wrap: true },
      { field: 'crop', headerName: 'Crop', width: 110 },
      { field: 'issue_to_processing', headerName: 'Issue to Processing', minWidth: 160, flex: 1, wrap: true },
      { field: 'stack_or_pallet', headerName: 'Stack or Pallet', width: 140 },
    ],
  },
  Consignment: {
    pageKey: 'Consignment',
    label: 'Consignment',
    sourceForm: 'frmPurchaseQryNPE',
    sourceQuery: 'PurchaseQryConsignment',
    // Access `frmPurchaseQryNPE` (RecordSource `PurchaseQryConsignment`) — không có
    // ColumnOrder tĩnh → dùng TabIndex per control (0→34, consignment_p_id=0 mặc định
    // đầu vì không khai TabIndex, giống pattern PendingPayment `id`).
    columns: [
      { field: 'consignment_p_id', headerName: 'ID', width: 90 },
      { field: 'contract_no', headerName: 'NPE No.', width: 150 },
      { field: 'contract_date', headerName: 'Contract Date', kind: 'date', width: 130 },
      { field: 'cp_code', headerName: 'CP Code', width: 120 },
      { field: 'name', headerName: 'CP Name', minWidth: 200, flex: 1, proper: true, wrap: true },
      num('quantity_kg', 'Quantity', 2, 140, true),
      { field: 'quality_code', headerName: 'Quality Code', width: 130 },
      { field: 'certification', headerName: 'Certification', width: 140 },
      { field: 'package_name', headerName: 'Package', minWidth: 150, flex: 1, wrap: true },
      { field: 'delivery_time', headerName: 'Delivery Time', kind: 'date', width: 130 },
      { field: 'delivery_place', headerName: 'Delivery Place Name', minWidth: 180, flex: 1, wrap: true },
      { field: 'shipper_code', headerName: 'Shipper Code', width: 130 },
      { field: 'shipper_name', headerName: 'Shipper Name', minWidth: 180, flex: 1, proper: true, wrap: true },
      { field: 'note', headerName: 'Note', minWidth: 180, flex: 1, wrap: true },
      num('stoploss_rate', 'Stoploss Rate', 4, 140),
      { field: 'interest_rate_note', headerName: 'Interest Rate Note', minWidth: 180, flex: 1, wrap: true },
      num('provisional_market_rate', 'Provisional Market Rate', 4, 190),
      num('storage_charge', 'Storage Charge', 2, 150),
      num('storage_loss', 'Storage Loss', 2, 140),
      num('two_way_handling_fee', 'Two-way Handling Fee', 2, 190),
      num('insurance_fee', 'Insurance Fee', 2, 150),
      num('received', 'Received (KG)', 2, 140, true),
      num('to_be_receive', 'To be Receive', 2, 150, true),
      num('fixed', 'Fixed (KG)', 2, 130, true),
      num('to_be_fix', 'To be Fix (KG)', 2, 140, true),
      num('advanced', 'Advanced', 2, 140, true),
      num('to_be_advance', 'To be Advance', 2, 150, true),
      num('paid_vnd', 'Paid Amount', 2, 150, true),
      num('allocated_paid', 'Allocated Paid', 2, 150, true),
      { field: 'signed_by_gm_text', headerName: 'Signed by GM', width: 130 },
      { field: 'sent_to_client_text', headerName: 'Sent to client', width: 130 },
      { field: 'signed_by_client_text', headerName: 'Signed by client', width: 140 },
      { field: 'trader_in_charge_name', headerName: 'Trader in Charge', minWidth: 180, flex: 1, proper: true, wrap: true },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 150, flex: 1, proper: true, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
    ],
  },
  PTBFFixation: {
    pageKey: 'PTBFFixation',
    label: 'PTBF Fixation (Purchase)',
    sourceForm: 'frmProcurementQryRefPTBFFixation',
    sourceQuery: 'ProcurementQryRefPTBFFixation',
    columns: [
      { field: 'fixation_date', headerName: 'Date', kind: 'date', width: 120 },
      { field: 'contract_no', headerName: 'Contract No', width: 150 },
      { field: 'supplier_name', headerName: 'CP Name', minWidth: 200, flex: 1, proper: true, wrap: true },
      { field: 'quality_code', headerName: 'Quality Code', width: 130 },
      num('total_lot', 'Total lot', 0, 120),
      num('lot_to_be_fixed', 'Lot to be fixed', 0, 140),
      num('lot_fixed', 'Lot fixed', 0, 120),
      num('market_level', 'Market level', 2, 130),
      { field: 'cross_check_fix_level', headerName: 'Cross check fixed level', minWidth: 180, flex: 1, wrap: true },
      { field: 'futures_month', headerName: 'Futures month', width: 140 },
      num('ptbf_diffs', 'Diffs', 2, 120),
      num('final_price', 'Final price', 2, 130),
      { field: 'note', headerName: 'Note', minWidth: 180, flex: 1, wrap: true },
      { field: 'ptbf_p_id', headerName: 'ID', width: 90 },
    ],
  },
  CSFixation: {
    pageKey: 'CSFixation',
    label: 'CS Fixation',
    sourceForm: 'frmPurchaseQryNPEFixation',
    sourceQuery: 'PurchaseQryCSFixation',
    // Access `frmPurchaseQryNPEFixation` ColumnOrder 0→19: cs_fixation_id trước
    // fixation_date; note nằm sau p_paid (trước person_update/date_update).
    columns: [
      { field: 'cs_fixation_id', headerName: 'ID', width: 90 },
      { field: 'fixation_date', headerName: 'Date', kind: 'date', width: 120 },
      { field: 'fixation_no', headerName: 'Fixation No', width: 140 },
      { field: 'contract_no', headerName: 'Contract No', width: 150 },
      { field: 'p_contract_no', headerName: 'P For Contract', width: 150 },
      { field: 'cp_code', headerName: 'CP Code', width: 120 },
      { field: 'name', headerName: 'CP Name', minWidth: 200, flex: 1, proper: true, wrap: true },
      { field: 'quality_code', headerName: 'Qualty', width: 120 },
      num('quantity_kg', 'Kg', 0, 130, true),
      num('price', 'Price', 0, 130),
      num('price_after_interest', 'Final Price (after Interest)', 0, 200),
      num('to_be_paid', 'P - TobeRequested (vnd)', 0, 190, true),
      num('total_amount', 'Total Amount', 0, 150, true),
      num('allocated_interest', 'Allocated Interest', 0, 160, true),
      num('allocated_amount', 'Allocated Advanced (incl Interest)', 0, 220, true),
      num('allocated_quantity', 'Allocated Quantity', 0, 160),
      num('p_paid', 'P - Requested (vnd)', 0, 170, true),
      { field: 'note', headerName: 'Fixation Note', minWidth: 180, flex: 1, wrap: true },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 150, flex: 1, proper: true, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
    ],
  },
  CPList: {
    pageKey: 'CPList',
    label: 'CP List',
    sourceForm: 'frmProcurementQryRefCPList',
    sourceQuery: 'ProcurementQryRefCPList',
    columns: [
      { field: 'code', headerName: 'Code', width: 130 },
      { field: 'name_cp', headerName: 'Name', minWidth: 220, flex: 1, proper: true, wrap: true },
      { field: 'short_name', headerName: 'Short Name', minWidth: 160, flex: 1, proper: true, wrap: true },
      { field: 'address', headerName: 'Address', minWidth: 220, flex: 1, wrap: true },
      { field: 'phone', headerName: 'Phone', width: 140 },
      { field: 'mobile', headerName: 'Mobile', width: 140 },
      { field: 'fax', headerName: 'Fax', width: 140 },
      { field: 'vat_code', headerName: 'VAT Code', width: 140 },
      { field: 'email', headerName: 'Email', minWidth: 180, flex: 1, wrap: true },
      { field: 'status', headerName: 'Status', width: 120 },
      { field: 'rep', headerName: 'Rep', minWidth: 160, flex: 1, proper: true, wrap: true },
      { field: 'position', headerName: 'Position', minWidth: 140, flex: 1, wrap: true },
      { field: 'reg_no', headerName: 'Reg No', width: 140 },
      { field: 'issued_by', headerName: 'Issued By', minWidth: 180, flex: 1, wrap: true },
      { field: 'issuing_date', headerName: 'Issuing Date', kind: 'date', width: 140 },
      { field: 'id_no', headerName: 'ID No', width: 140 },
      { field: 'id_place', headerName: 'ID Place', minWidth: 160, flex: 1, wrap: true },
      { field: 'id_date', headerName: 'ID Date', kind: 'date', width: 140 },
      { field: 'note', headerName: 'Note', minWidth: 180, flex: 1, wrap: true },
      { field: 'sale_offshore_onshore', headerName: 'Sale Offshore Onshore', minWidth: 200, flex: 1, wrap: true },
      { field: 'type_cp', headerName: 'Type', width: 120 },
      { field: 'status_cp', headerName: 'Using', width: 120 },
      { field: 'date_update', headerName: 'Updated Day', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 150, flex: 1, proper: true, wrap: true },
    ],
  },
  CPBank: {
    pageKey: 'CPBank',
    label: 'CP Bank',
    sourceForm: 'frmProcurementQryRefCPBank',
    sourceQuery: 'ProcurementQryRefCPBank',
    columns: [
      { field: 'cp_code', headerName: 'CP Code', width: 130 },
      { field: 'name', headerName: 'CP Name', minWidth: 220, flex: 1, proper: true, wrap: true },
      { field: 'short_name', headerName: 'Short Name', minWidth: 160, flex: 1, proper: true, wrap: true },
      { field: 'bank', headerName: 'Bank', minWidth: 160, flex: 1, wrap: true },
      { field: 'branch', headerName: 'Branch', minWidth: 160, flex: 1, wrap: true },
      { field: 'bank_account', headerName: 'Bank Account', width: 170 },
      { field: 'account_name', headerName: 'Account Name', minWidth: 180, flex: 1, proper: true, wrap: true },
      { field: 'note', headerName: 'Note', minWidth: 180, flex: 1, wrap: true },
      ...upd(),
    ],
  },
  FuturesAllocation: {
    pageKey: 'FuturesAllocation',
    label: 'Futures Allocation',
    sourceForm: 'frmProcurementQryRefFuturesAllocation',
    sourceQuery: 'ProcurementQryRefFuturesAllocation',
    columns: [
      { field: 'date', headerName: 'Date', kind: 'date', width: 120 },
      { field: 'contract_id', headerName: 'Contract ID', width: 150 },
      { field: 'ps', headerName: 'P/S', minWidth: 120, flex: 1, wrap: true },
      { field: 'cp', headerName: 'CP', minWidth: 120, flex: 1, proper: true, wrap: true },
      num('kg', 'Kg', 0, 120),
      { field: 'type', headerName: 'Type', width: 120 },
      num('lot_allocating', 'Lot Allocating', 0, 140),
      { field: 'futures_id', headerName: 'Futures ID', width: 140 },
      { field: 'buysell', headerName: 'Buy/Sell', width: 120 },
      { field: 'month', headerName: 'Month', width: 120 },
      num('price', 'Price', 2, 120),
      num('balance', 'Balance', 0, 140),
    ],
  },
  ListandValo: {
    pageKey: 'ListandValo',
    label: 'List and Valo',
    sourceForm: 'frmProcurementQryRefValuation',
    sourceQuery: 'ProcurementQryRefValuation',
    columns: [
      { field: 'code', headerName: 'Code', width: 120 },
      { field: 'description', headerName: 'Description', minWidth: 220, flex: 1, wrap: true },
      { field: 'bean_size', headerName: 'Bean Size', width: 120 },
      { field: 'phase', headerName: 'Phase', width: 120 },
      { field: 'category', headerName: 'Category', width: 120 },
      num('ranked', 'Rranked', 0, 100),
      num('premium_vs_g2', 'Premium vs G2 (New)', 4, 180),
      num('valuation_in_use', 'Valuation in Use (New)', 4, 190),
      { field: 'special_requirements', headerName: 'Special Requirements', minWidth: 220, flex: 1, wrap: true },
      { field: 'note', headerName: 'Note', minWidth: 180, flex: 1, wrap: true },
      { field: 'description_on_contract', headerName: 'Description on Contract', minWidth: 220, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 150, flex: 1, proper: true, wrap: true },
    ],
  },
  FuturesList: {
    pageKey: 'FuturesList',
    label: 'Futures List',
    sourceForm: 'frmProcurementQryRefCalendar',
    sourceQuery: 'ProcurementQryRefCalendar',
    columns: [
      num('year', 'Year', 0, 100),
      num('month', 'Month', 0, 100),
      { field: 'code', headerName: 'Code', width: 120 },
      { field: 'first_business_day', headerName: 'First Business Day', kind: 'date', width: 160 },
      { field: 'last_business_day', headerName: 'Last Business Day', kind: 'date', width: 160 },
      { field: 'first_notice_day', headerName: 'First Notice Day', kind: 'date', width: 150 },
      { field: 'last_trading_day', headerName: 'Last Trading Day', kind: 'date', width: 150 },
      { field: 'last_fixing_day', headerName: 'Last Fixing Day', kind: 'date', width: 150 },
      { field: 'ex', headerName: 'Exchange', width: 120 },
      { field: 'ex_text', headerName: 'Exchange Text', minWidth: 160, flex: 1, wrap: true },
      { field: 'full_symbol', headerName: 'Full symbol', minWidth: 150, flex: 1, wrap: true },
      { field: 'date_updated', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_updated', headerName: 'Updated Person', minWidth: 150, flex: 1, proper: true },
    ],
  },
  Bank: {
    pageKey: 'Bank',
    label: 'Bank',
    sourceForm: 'frmProcurementQryRefBank',
    sourceQuery: 'ProcurementQryRefBank',
    columns: [
      // Không có ColumnOrder tĩnh → dùng TabIndex: bank(0) bank_name(1)
      // bank_name_eng(2) date_update(3) person_update(4).
      { field: 'bank', headerName: 'Bank Code', width: 140 },
      { field: 'bank_name', headerName: 'Bank Name', minWidth: 220, flex: 1, wrap: true },
      { field: 'bank_name_eng', headerName: 'Bank Name English', minWidth: 220, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 150, flex: 1, proper: true, wrap: true },
    ],
  },
  Package: {
    pageKey: 'Package',
    label: 'Package',
    sourceForm: 'frmProcurementQryRefPackage',
    sourceQuery: 'ProcurementQryRefPackage',
    columns: [
      { field: 'packaging_code', headerName: 'Packaging Code', width: 160 },
      { field: 'packaging', headerName: 'Packaging', minWidth: 200, flex: 1, wrap: true },
      num('prem_discount', 'Prem Discount', 0, 140),
      num('nw_per_bag_kg', 'Nw Per Bag Kg', 0, 140),
      { field: 'description_eng', headerName: 'Description Eng', minWidth: 220, flex: 1, wrap: true },
      { field: 'description_vn', headerName: 'Description Vn', minWidth: 220, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 150, flex: 1, proper: true, wrap: true },
    ],
  },
  DeliveryPlace: {
    pageKey: 'DeliveryPlace',
    label: 'Delivery Place',
    sourceForm: 'frmProcurementQryRefDeliveryPlace',
    sourceQuery: 'ProcurementQryRefDeliveryPlace',
    columns: [
      { field: 'delivery_place_code', headerName: 'Delivery Place Code', width: 180 },
      { field: 'delivery_place', headerName: 'Delivery Place', minWidth: 220, flex: 1, wrap: true },
      num('logistic_costs_to_fob', 'Logistic Costs to_FOB', 2, 180),
      { field: 'address', headerName: 'Address', minWidth: 220, flex: 1, wrap: true },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 150, flex: 1, proper: true, wrap: true },
    ],
  },
  Certification: {
    pageKey: 'Certification',
    label: 'Certification',
    sourceForm: 'frmProcurementQryRefCertification',
    sourceQuery: 'MdQryCertification',
    columns: [
      { field: 'certification', headerName: 'Certification', width: 160 },
      num('premium', 'Premium (New)', 4, 160),
      num('si', 'SI', 4, 120),
      num('sd', 'SD', 4, 120),
      { field: 'max_date_change', headerName: 'Date Change (New)', kind: 'date', width: 160 },
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime', width: 160 },
      { field: 'person_update', headerName: 'Updated Person', minWidth: 150, flex: 1, proper: true, wrap: true },
    ],
  },
}

export function getProcurementReferencePageConfig(
  pageKey: ProcurementReferencePageKey,
): ProcurementReferencePageConfig {
  return PROCUREMENT_REFERENCE_PAGE_CONFIGS[pageKey]
}
